import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

export default function RiderPWA() {
  const { currentUser, firebaseUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  useEffect(() => {
    if (firebaseUser && currentUser && currentUser.role === 'rider') {
      fetchOrders();
    }
    // eslint-disable-next-line
  }, [firebaseUser, currentUser]);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.get(`${API_ENDPOINTS.orders}/rider-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (err) {
      setError('Failed to fetch assigned orders');
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdatingOrderId(orderId);
    try {
      const token = await firebaseUser.getIdToken();
      await axios.patch(
        `${API_ENDPOINTS.orders}/${orderId}/delivery-status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchOrders();
    } catch (err) {
      setError('Failed to update order status');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  if (!firebaseUser) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00ffe7]"></div>
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'rider') {
    return (
      <div className="max-w-xl mx-auto py-24 text-center">
        <h1 className="text-3xl font-bold mb-4 text-red-600">Unauthorized</h1>
        <p className="text-gray-700">You must be a rider to access this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-12">
      <h1 className="text-4xl font-extrabold mb-8 text-center text-[#00bfae]">NexGENGaming Rider App</h1>
      <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
        <h2 className="text-2xl font-bold mb-4">Assigned Orders</h2>
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00ffe7]"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 mb-4">{error}</div>
        ) : orders.length === 0 ? (
          <div className="text-gray-500">No assigned orders.</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {orders.map(order => (
              <li key={order._id} className="py-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-mono text-sm text-gray-700">Order #{order._id.slice(-6)}</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    order.status === 'paid' ? 'bg-green-100 text-green-800' :
                    order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                    order.status === 'delivered' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>
                <div className="text-gray-700 mb-1">
                  <span className="font-semibold">Customer:</span> {order.user?.name || 'N/A'} <span className="text-xs text-gray-400">({order.user?.email})</span>
                </div>
                <ul className="list-disc ml-6 text-gray-600 text-sm mb-2">
                  {order.items.map(item => (
                    <li key={item._id}>
                      {item.product?.name} ({item.variant.color}, {item.variant.size}) x {item.quantity}
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-gray-400 mb-2">Placed on {new Date(order.createdAt).toLocaleString()}</div>
                <button
                  className="text-[#00bfae] underline text-sm mb-2"
                  onClick={() => handleExpand(order._id)}
                >
                  {expandedOrderId === order._id ? 'Hide Details' : 'View Details'}
                </button>
                {expandedOrderId === order._id && (
                  <div className="bg-gray-50 rounded-lg p-4 mt-2">
                    <div className="mb-2">
                      <span className="font-semibold">Order ID:</span> {order._id}
                    </div>
                    <div className="mb-2">
                      <span className="font-semibold">Customer Email:</span> {order.user?.email}
                    </div>
                    <div className="mb-2">
                      <span className="font-semibold">Order Status:</span> {order.status}
                    </div>
                    <div className="mb-2">
                      <span className="font-semibold">Order Items:</span>
                      <ul className="list-disc ml-6 text-gray-700 text-sm">
                        {order.items.map(item => (
                          <li key={item._id}>
                            {item.product?.name} ({item.variant.color}, {item.variant.size}) x {item.quantity}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Add more details as needed, e.g., address */}
                    {order.status === 'shipped' && (
                      <div className="flex gap-4 mt-4">
                        <button
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-semibold disabled:opacity-60"
                          onClick={() => handleStatusUpdate(order._id, 'delivered')}
                          disabled={updatingOrderId === order._id}
                        >
                          Mark as Delivered
                        </button>
                        <button
                          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-semibold disabled:opacity-60"
                          onClick={() => handleStatusUpdate(order._id, 'undelivered')}
                          disabled={updatingOrderId === order._id}
                        >
                          Mark as Undelivered
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Order Details</h2>
        <div className="text-gray-500">[Order detail view will be displayed here]</div>
      </div>
    </div>
  );
} 